
import React, { useState, useRef, useEffect } from 'react';
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import SearchBar from "@/components/SearchBar";
import { CompanySettingsProvider } from "@/context/CompanySettingsContext";
import { useAuth } from "@/context/AuthContext";
import Header from '@/components/Header';
import { StarBackground } from '@/components/ui/star-background';
import { AppSidebar } from '@/components/AppSidebar';
import { useAnimatedSidebar } from '@/components/ui/animated-sidebar';
import { motion } from 'framer-motion';
import AnimatedTextCycle from '@/components/ui/animated-text-cycle';
import CategoryButton from '@/components/CategoryButton';

const Index: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState('');
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();
  const searchBarRef = useRef<{ focus: () => void }>(null);
  const { open, width } = useAnimatedSidebar();

  const aiHeadlines = [
    "KI-gestützte Versicherungs-E-Mails",
    "Intelligente Vergleichsanalysen",
    "Automatisierte Angebotserstellung",
    "Digitale Vertragssignaturen",
    "Smarte Kundenberatung",
    "Personalisierte Versicherungslösungen"
  ];

  const highlightedWords = [
    "nachhaltig",
    "professionell", 
    "visuell", 
    "dauerhaft", 
    "effektiv"
  ];

  useEffect(() => {
    console.log("Index: Component mounted, will focus search bar in 1000ms");
    const timer = setTimeout(() => {
      if (searchBarRef.current) {
        console.log("Index: Initial focus on search bar");
        searchBarRef.current.focus();
      } else {
        console.log("Index: searchBarRef is null on initial focus attempt");
      }
    }, 1000);
    
    return () => clearTimeout(timer);
  }, []);

  const handleSearchSubmit = async (prompt: string) => {
    console.log("Index: handleSearchSubmit called with prompt:", prompt);
    
    if (!prompt.trim()) {
      console.log("Index: Empty prompt received, not processing");
      return;
    }

    if (!user) {
      console.log("Index: No user logged in, showing login toast");
      toast({
        title: "Anmeldung erforderlich",
        description: "Bitte melden Sie sich an, um E-Mails zu erstellen.",
        variant: "destructive"
      });
      navigate('/auth');
      return;
    }
    
    console.log("Index: Search submitted with prompt:", prompt);
    setIsLoading(true);
    
    try {
      // Clear any existing chat ID to ensure a new conversation is started
      localStorage.removeItem('currentChatId');
      localStorage.setItem('currentPrompt', prompt);
      localStorage.setItem('isGenerating', 'true');
      
      // Emit event to notify other components about the new conversation
      window.dispatchEvent(new CustomEvent('start-new-conversation'));
      
      console.log("Index: Navigating to /builder");
      navigate('/builder');
    } catch (error) {
      console.error('Failed to prepare for navigation:', error);
      toast({
        title: "Fehler",
        description: "Navigation zum Builder fehlgeschlagen. Bitte versuchen Sie es später erneut.",
        variant: "destructive"
      });
      setIsLoading(false);
    }
  };

  const handleCategoryClick = (category: string) => {
    const categoryPrompts: Record<string, string> = {
      "Vergleich": "Erstelle ein HTML E-Mail-Template für einen Versicherungsvergleich mit Tabelle und Call-to-Action Button",
      "Willkommens-Mail": "Erstelle ein HTML E-Mail-Template für eine Willkommens-E-Mail für neue Versicherungskunden mit personalisierter Anrede",
      "Terminanfrage": "Erstelle ein HTML E-Mail-Template für eine Anfrage zu einem Beratungstermin mit Terminoptionen",
      "Angebot": "Erstelle ein HTML E-Mail-Template für ein personalisiertes Versicherungsangebot mit Preisübersicht",
      "Newsletter": "Erstelle ein HTML E-Mail-Template für einen monatlichen Versicherungs-Newsletter mit aktuellen Neuigkeiten",
      "Kundenangebot": "Erstelle ein HTML E-Mail-Template für ein exklusives Angebot für Bestandskunden mit Sonderkonditionen"
    };
    
    console.log("Index: Category clicked:", category);
    const prompt = categoryPrompts[category] || `Erstelle ein HTML E-Mail-Template zum Thema ${category}`;
    setSelectedPrompt(prompt);
    
    setTimeout(() => {
      if (searchBarRef.current) {
        console.log("Index: Focusing search bar after category selection");
        searchBarRef.current.focus();
      } else {
        console.log("Index: Failed to focus - searchBarRef is null after category selection");
      }
    }, 150);
  };

  const sidebarWidth = user && open ? width.expanded : user ? width.collapsed : "0px";

  const contentVariants = {
    hidden: { 
      opacity: 0, 
      y: 30,
      scale: 0.95
    },
    visible: { 
      opacity: 1, 
      y: 0,
      scale: 1,
      transition: {
        duration: 0.7,
        ease: "easeOut"
      }
    }
  };

  return (
    <CompanySettingsProvider>
      <div className="min-h-screen bg-black relative overflow-x-hidden">
        <StarBackground />
        <Header />
        <AppSidebar />
        
        <div className="flex min-h-screen w-full">
          <div 
            className="flex-1 flex flex-col items-center justify-center p-4 min-h-screen w-full"
            style={{ 
              marginLeft: sidebarWidth,
              width: user ? `calc(100% - ${sidebarWidth})` : "100%",
              transition: "margin-left 0.3s ease, width 0.3s ease"
            }}
          >
            <motion.div 
              className="w-full max-w-5xl mx-auto text-center px-4 sm:px-6 flex flex-col items-center justify-center space-y-8 sm:space-y-10 md:space-y-12"
              variants={contentVariants}
              initial="hidden"
              animate="visible"
            >
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light relative inline-flex flex-wrap justify-center items-center gap-2 sm:gap-3 md:gap-4 headline-glow">
                <span className="text-white whitespace-nowrap">Versicherungskunden</span>
                <AnimatedTextCycle 
                  words={highlightedWords}
                  interval={3000}
                  className="text-jade font-bold shadow-jade"
                />
                <span className="text-white whitespace-nowrap">beeindrucken</span>
              </h1>

              <div className="w-full max-w-2xl mx-auto">
                <SearchBar 
                  ref={searchBarRef}
                  onSubmit={handleSearchSubmit} 
                  isLoading={isLoading} 
                  initialValue={selectedPrompt}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 w-full max-w-3xl">
                {aiHeadlines.map((headline, index) => (
                  <CategoryButton
                    key={index}
                    label={headline}
                    className="w-full text-sm sm:text-base md:text-lg neo-blur hover:border-jade/30 transition-all duration-300"
                    onClick={() => {
                      setSelectedPrompt(`Erstelle ein HTML E-Mail-Template für ${headline}`);
                      if (searchBarRef.current) {
                        searchBarRef.current.focus();
                      }
                    }}
                  />
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </CompanySettingsProvider>
  );
};

export default Index;

